import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { supabase } from '../lib/supabase';
import { BrandMark } from '../components/BrandMark';
import { ChatWidget } from '../chat/ChatWidget';
import { ConsentBanner } from '../components/ConsentBanner';
import { LangSwitcher, LANGS } from '../components/LangSwitcher';
import { applySeo, LOCALE_PATHS, PARTNER_PATHS, SITE_URL } from '../lib/seo';
import { startAnalytics, pageview, track } from '../lib/analytics';

export function PartnersPage({ lang }: { lang: string }) {
  const { t, i18n } = useTranslation();
  const [org, setOrg] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (i18n.resolvedLanguage !== lang) void i18n.changeLanguage(lang);
  }, [lang, i18n]);

  useEffect(() => {
    applySeo({
      lang,
      title: t('partners.seo_title'),
      description: t('partners.seo_description'),
      paths: PARTNER_PATHS,
      ld: {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'La petite table — Parcerias',
        provider: { '@type': 'Organization', name: 'La petite table', url: SITE_URL },
        areaServed: 'BR',
        description: t('partners.seo_description')
      }
    });
    startAnalytics();
    pageview(PARTNER_PATHS[lang] ?? '/parceiros', t('partners.seo_title'));
  }, [lang, t]);

  const estPoints = t('partners.est_points', { returnObjects: true }) as string[];
  const estSteps = t('partners.est_steps', { returnObjects: true }) as string[];
  const assocPoints = t('partners.assoc_points', { returnObjects: true }) as string[];

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!org.trim()) return;
    setBusy(true);
    const { error } = await supabase.rpc('submit_partner_lead', {
      p_org: org, p_name: name, p_email: email, p_phone: phone, p_message: message, p_locale: lang
    });
    setBusy(false);
    if (!error) { setSent(true); track('partner_lead', { locale: lang }); }
  }

  return (
    <div className="mkt">
      <header className="mkt-header">
        <a className="brand" href={LOCALE_PATHS[lang] ?? '/'}>
          <BrandMark className="brand__mark" />
          <span className="brand__name">La <em>petite</em> <span className="brand__accent">table</span></span>
        </a>
        <div className="mkt-header__right">
          <LangSwitcher lang={lang} paths={PARTNER_PATHS} />
          <Link to="/login" className="mkt-login">{t('partners.login')}</Link>
        </div>
      </header>

      {/* Dobra 1 — aviso aos estabelecimentos */}
      <section className="mkt-hero">
        <div className="mkt-hero__text">
          <span className="mkt-badge">{t('partners.badge')}</span>
          <h1 className="mkt-hero__title">{t('partners.est_title')}</h1>
          <p className="mkt-hero__subtitle">{t('partners.est_subtitle')}</p>
          <ul className="partner-points">
            {estPoints.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
          <div className="mkt-hero__cta">
            <a href={(LOCALE_PATHS[lang] ?? '/') === '/' ? '/#demo' : `${LOCALE_PATHS[lang]}#demo`} className="btn btn--primary" onClick={() => track('demo_click', { from: 'partners' })}>{t('demo.title')}</a>
            <a href="#assoc" className="btn btn--ghost">{t('partners.assoc_title')}</a>
          </div>
        </div>
        <div className="mkt-hero__visual">
          <div className="coupon-card" aria-hidden="true">
            <span className="coupon-card__off">−10%</span>
            <span className="coupon-card__sub">{t('partners.badge')}</span>
          </div>
        </div>
      </section>

      <section className="mkt-section mkt-section--alt">
        <h2 className="mkt-section__title">{t('partners.est_how_title')}</h2>
        <div className="mkt-steps">
          {estSteps.map((s, i) => (
            <div key={i} className="mkt-step">
              <span className="mkt-step__num">{i + 1}</span>
              <div><p>{s}</p></div>
            </div>
          ))}
        </div>
        <p className="mkt-section__subtitle" style={{ marginTop: 16 }}>{t('partners.est_note')}</p>
      </section>

      {/* Dobra 2 — associações e sindicatos */}
      <section id="assoc" className="mkt-section">
        <h2 className="mkt-section__title">{t('partners.assoc_title')}</h2>
        <p className="mkt-section__subtitle">{t('partners.assoc_subtitle')}</p>
        <div className="mkt-grid mkt-grid--3" style={{ marginBottom: 28 }}>
          {assocPoints.map((p, i) => (
            <div key={i} className="mkt-card"><p>{p}</p></div>
          ))}
        </div>

        <div className="partner-form-wrap">
          <h3>{t('partners.form_title')}</h3>
          {sent ? (
            <p className="form-info">{t('partners.form_success')}</p>
          ) : (
            <form onSubmit={submit} className="form partner-form">
              <label className="field"><span>{t('partners.form_assoc')}</span><input required value={org} onChange={(e) => setOrg(e.target.value)} /></label>
              <div className="form-row">
                <label className="field"><span>{t('partners.form_name')}</span><input value={name} onChange={(e) => setName(e.target.value)} /></label>
                <label className="field"><span>{t('partners.form_email')}</span><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
                <label className="field"><span>{t('partners.form_phone')}</span><input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" /></label>
              </div>
              <label className="field"><span>{t('partners.form_message')}</span><textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} /></label>
              <button type="submit" className="btn btn--primary" disabled={busy || !org.trim()}>{busy ? t('partners.form_sending') : t('partners.form_submit')}</button>
            </form>
          )}
        </div>
      </section>

      <footer className="mkt-footer">
        <div className="brand">
          <BrandMark className="brand__mark" />
          <span className="brand__name">La <em>petite</em> <span className="brand__accent">table</span></span>
        </div>
        <div className="mkt-footer__links">
          {LANGS.map((l) => (<Link key={l.code} to={PARTNER_PATHS[l.code]}>{l.short}</Link>))}
        </div>
        <p className="mkt-footer__rights">© {t('app.name')} · {t('marketing.footer_rights')}</p>
      </footer>

      <ChatWidget lang={lang} />
      <ConsentBanner />
    </div>
  );
}
